import type { FC } from "react";

import { ArtistResolution } from "@/pages/tina/items/components/panels/hitl-panel/artist-resolution";
import { CuratorReviewResolution } from "@/pages/tina/items/components/panels/hitl-panel/curator-review-resolution";
import { EnrichmentResolution } from "@/pages/tina/items/components/panels/hitl-panel/enrichment-resolution";
import { ImageResolution } from "@/pages/tina/items/components/panels/hitl-panel/image-resolution";
import { LowScoreResolution } from "@/pages/tina/items/components/panels/hitl-panel/low-score-resolution";
import { MovieResolution } from "@/pages/tina/items/components/panels/hitl-panel/movie-resolution";
import { PersonResolution } from "@/pages/tina/items/components/panels/hitl-panel/person-resolution";
import { PlayResolution } from "@/pages/tina/items/components/panels/hitl-panel/play-resolution";
import { RefreshResolution } from "@/pages/tina/items/components/panels/hitl-panel/refresh-resolution";
import { VenueResolution } from "@/pages/tina/items/components/panels/hitl-panel/venue-resolution";
import type { HitlContext, HitlReason, PipelineItem } from "@/types/tina";

export interface ResolutionComponentProps {
  item: PipelineItem;
  hitlContext: HitlContext;
  onResolved: () => void;
}

// Phases 1–4 register per-reason components here. Phase 1 ships the venue
// reasons; remaining reasons fall back to the read-only context + force-close
// affordance below.
export const RESOLUTION_COMPONENTS: Partial<
  Record<HitlReason, FC<ResolutionComponentProps>>
> = {
  venue_no_address: VenueResolution,
  venue_places_miss: VenueResolution,
  venue_low_confidence: VenueResolution,
  venue_geodata_miss: VenueResolution,
  artist_ambiguous_match: ArtistResolution,
  artist_spotify_miss: ArtistResolution,
  artist_spotify_no_image: ArtistResolution,
  artist_low_confidence: ArtistResolution,
  movie_tmdb_miss: MovieResolution,
  movie_low_confidence: MovieResolution,
  movie_ambiguous_match: MovieResolution,
  play_ambiguous_match: PlayResolution,
  play_no_image_found: PlayResolution,
  person_ambiguous_match: PersonResolution,
  low_score: LowScoreResolution,
  image_low_confidence: ImageResolution,
  // One entry covers all five gated enrichment flows:
  // `enrichment_low_confidence` is deliberately a single reason, and the real
  // entity is `hitl_context.problem.entity_type`.
  enrichment_low_confidence: EnrichmentResolution,
  // Parks on its own `enrichment_artist_curation` job when an automatic write
  // disagreed with a curated field (artist-field-locks, 2026-09-28).
  enrichment_curator_review: CuratorReviewResolution,
  // The only reason whose subject is an event that is ALREADY published: the
  // panel confirms changes against the live Mood event rather than deciding
  // whether to create one.
  refresh_review: RefreshResolution,
};

export const FallbackResolution: FC<ResolutionComponentProps> = () => (
  <p className="text-sm text-muted-foreground">
    There is no resolution UI yet for this reason. Use Force close, or resolve
    it from the Mood backend directly.
  </p>
);

export function resolutionComponentFor(
  reason: HitlReason,
): FC<ResolutionComponentProps> {
  return RESOLUTION_COMPONENTS[reason] ?? FallbackResolution;
}
